import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Box, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import deepmerge from 'deepmerge';

import useNotification from '../../../../../core/hooks/use_notification';
import { ROUTE_PRODUCT_PROMOTIONS } from '../../../../../core/constants';
import { getDefaultPaginationQueryValues } from '../../../../../core/utils/api_utils';
import { getPodsNotificationData, getPriceNotificationData, getProductNotificationData, getProductPromotionsNotificationData } from '../../../../../core/utils/notification_utils';
import PaginationControls from '../../../../common/pagination_controls';
import { getAllPodsAction, getAllPodsState } from '../../../../../features/pod/get_all_pods';
import PromotionsFormDialog from './form/promotions_form_dialog';
import DeletePromotionsFormDialog from './form/delete_promotions_form_dialog';
import { getPriceAction, getPriceState } from '../../../../../features/price/get_price';
import { getProductPromotionsAction, getProductPromotionsState, invalidateProductPromotions, } from '../../../../../features/price/get_product_promotions';
import ProductPromotionsFilters from './product_promotions_filters';
import ProductPromotionsList from './product_promotions_list';
import { getProductAction, getProductState } from '../../../../../features/product/get_product';
import PageHeader from '../../../../common/page_header';

const initialQueryParams = {
    ...getDefaultPaginationQueryValues,
    podId: '',
};

const ProductPromotionsDetailsPage = () => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();
    const navigate = useNavigate();
    const { productId } = useParams();
    const location = useLocation();

    const getProduct = useSelector(getProductState);
    const getProductPromotions = useSelector(getProductPromotionsState);
    const getAllPods = useSelector(getAllPodsState);
    const getPrice = useSelector(getPriceState);

    const [queryParams, setQueryParams] = useState({ ...initialQueryParams, productId });

    const refreshList = useCallback(() => {
        dispatch(getProductPromotionsAction(queryParams))
            .then(response => showErrorMessage(getProductPromotionsNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    useEffect(() => {
        refreshList();

        return () => {
            dispatch(invalidateProductPromotions());
        };
    }, [dispatch, refreshList]);

    useEffect(() => {
        dispatch(getProductAction({ id: productId }))
            .then(respone => showErrorMessage(getProductNotificationData(respone)));
        dispatch(getAllPodsAction())
            .then(response => showErrorMessage(getPodsNotificationData(response)));
        dispatch(getPriceAction(productId))
            .then(response => showErrorMessage(getPriceNotificationData(response)));
    }, [dispatch, productId, showErrorMessage]);

    const renderData = () => {
        if (getProduct?.loading || getAllPods?.loading || getPrice?.loading) {
            return (
                <Box mt={ 6 } width="100%">
                    <LinearProgress/>
                </Box>
            );
        }

        if (getAllPods?.data && getPrice?.data && getProduct?.data) {
            return (
                <>
                    <Paper variant="outlined" sx={ { p: 2, mb: 3, backgroundColor: 'slate.50' } }>
                        <Stack direction="row" spacing={ 1 } alignItems="center">
                            <Typography variant="body1" sx={ { fontWeight: 600 } }>Promocja na towar:</Typography>
                            <Typography variant="body1">{ getProduct?.data.name }</Typography>
                        </Stack>
                    </Paper>

                    <Box sx={ {
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3
                    } }>
                        <Box sx={ {
                            flexGrow: 1,
                            minWidth: { xs: '100%', md: '300px' },
                            maxWidth: { xs: '100%', md: '40%' }
                        } }>
                            <ProductPromotionsFilters
                                onSelectPodChange={ podId => setQueryParams(prevState => deepmerge(prevState, {
                                    podId,
                                    page: 0
                                })) }
                            />
                        </Box>

                        <Stack direction="row" spacing={ 2 } sx={ { width: { xs: '100%', md: 'auto' } } }>
                            <PromotionsFormDialog
                                productId={ productId }
                                purchasePrice={ getProduct?.data?.purchaseGrossPrice }
                                refreshList={ refreshList }
                            />
                            <DeletePromotionsFormDialog
                                productId={ productId }
                                refreshList={ refreshList }
                            />
                        </Stack>
                    </Box>

                    { getProductPromotions?.loading && <LinearProgress sx={ { mb: 2 } }/> }

                    {
                        (getProductPromotions?.data?.content) && (
                            <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
                                <ProductPromotionsList refreshList={ refreshList }/>
                                <Box sx={ { display: 'flex', justifyContent: 'center' } }>
                                    <PaginationControls
                                        currentPage={ getProductPromotions?.data?.pageable?.pageNumber }
                                        totalPages={ getProductPromotions?.data?.totalPages }
                                        setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                                    />
                                </Box>
                            </Box>
                        )
                    }
                </>
            );
        }

        return null;
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3, mb: 4 } }>
            <PageHeader
                title="Szczegóły promocji"
                showBack={ true }
                onBack={ () => navigate(ROUTE_PRODUCT_PROMOTIONS, { state: { queryParams: location?.state?.queryParams } }) }
            />
            { renderData() }
        </Box>
    );
};

export default ProductPromotionsDetailsPage;